import React from 'react'
import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer'
import moment from 'moment'
import ambunetWordmark from '../../../assets/images/ambunetWordmark.png'

// Printable pick list for one vehicle/asset, built from the live check panel
// (MakeReadyCheckPanel.jsx). Four lists, each something to do on the floor:
// pick what's short, pull what's expired, swap what's about to expire, and
// sort out equipment that isn't right. Tick boxes are for pen on paper.
//
// Layout follows the react-pdf pagination rules in the IncidentPDF notes:
// every heading and row is a direct child of <Page>, rows are wrap={false}
// (bounded height), headings use minPresenceAhead so they never sit alone at
// the foot of a page, and stacked blocks are spaced with top margins.

const INK = '#1F2A2C'
const MUTED = '#64748B'
const RULE = '#E2E8F0'
const SOFT = '#F8FAFC'
const SLATE = '#3E4C4E'

const TONES = {
  pick: { hex: '#85276A', tint: '#F6EDF3', label: 'To pick' },
  expired: { hex: '#E32525', tint: '#FDECEC', label: 'Expired' },
  soon: { hex: '#F28B1F', tint: '#FEF4E8', label: 'Expiring soon' },
  equipment: { hex: '#268EBB', tint: '#EAF4F9', label: 'Equipment issues' },
}

const styles = StyleSheet.create({
  page: {
    paddingTop: 36,
    paddingBottom: 64,
    paddingHorizontal: 36,
    fontSize: 10,
    fontFamily: 'Helvetica',
    color: INK,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  orgLogo: { maxHeight: 44, maxWidth: 160, objectFit: 'contain' },
  orgName: { fontSize: 13, fontFamily: 'Helvetica-Bold', color: SLATE },
  kicker: {
    fontSize: 8,
    letterSpacing: 1.5,
    color: MUTED,
    textAlign: 'right',
  },
  title: {
    fontSize: 22,
    fontFamily: 'Helvetica-Bold',
    color: INK,
    textAlign: 'right',
  },
  rule: { height: 2, backgroundColor: SLATE, marginTop: 12, borderRadius: 1 },

  subject: {
    marginTop: 14,
    padding: 12,
    backgroundColor: SOFT,
    borderRadius: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  subjectLabel: { fontSize: 20, fontFamily: 'Helvetica-Bold' },
  subjectSub: { fontSize: 10, color: MUTED, marginTop: 2 },
  metaLabel: { fontSize: 7, color: MUTED, letterSpacing: 1, textAlign: 'right' },
  metaValue: { fontSize: 10, textAlign: 'right', marginTop: 1 },

  tiles: { flexDirection: 'row', marginTop: 12 },
  tile: {
    flex: 1,
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderTopWidth: 3,
  },
  tileValue: { fontSize: 18, fontFamily: 'Helvetica-Bold' },
  tileLabel: { fontSize: 8, color: MUTED, marginTop: 1 },

  blockHead: {
    marginTop: 18,
    flexDirection: 'row',
    alignItems: 'center',
  },
  blockDot: { width: 8, height: 8, borderRadius: 4, marginRight: 6 },
  blockTitle: { fontSize: 13, fontFamily: 'Helvetica-Bold' },
  blockHint: { fontSize: 9, color: MUTED, marginLeft: 6 },

  colHead: {
    marginTop: 6,
    flexDirection: 'row',
    paddingVertical: 5,
    paddingHorizontal: 6,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
  colHeadText: {
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 0.8,
    color: SLATE,
  },
  sectionRow: {
    flexDirection: 'row',
    paddingTop: 6,
    paddingBottom: 3,
    paddingHorizontal: 6,
    borderBottomWidth: 1,
    borderBottomColor: RULE,
  },
  sectionText: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: MUTED },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 5,
    paddingHorizontal: 6,
    borderBottomWidth: 1,
    borderBottomColor: RULE,
  },
  box: {
    width: 10,
    height: 10,
    borderWidth: 1,
    borderColor: SLATE,
    borderRadius: 2,
  },
  qty: { fontFamily: 'Helvetica-Bold', textAlign: 'right' },

  empty: {
    marginTop: 18,
    padding: 16,
    borderRadius: 6,
    backgroundColor: '#E8F5EE',
    borderLeftWidth: 4,
    borderLeftColor: '#00964C',
  },

  signOff: {
    marginTop: 22,
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: RULE,
    paddingTop: 12,
  },
  signField: { flex: 1, marginRight: 14 },
  signLine: { borderBottomWidth: 1, borderBottomColor: SLATE, height: 18 },
  signLabel: { fontSize: 8, color: MUTED, marginTop: 3 },

  footer: {
    position: 'absolute',
    bottom: 24,
    left: 36,
    right: 36,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: RULE,
    paddingTop: 8,
  },
  poweredBy: { flexDirection: 'row', alignItems: 'center' },
  poweredText: { fontSize: 8, color: MUTED, marginRight: 6 },
  wordmark: { height: 20 },
  footerText: { fontSize: 7.5, color: MUTED },
})

// Column layouts per list: [label, flex/width style, align].
const COLUMNS = {
  pick: [
    ['', { width: 18 }],
    ['ITEM', { flex: 1 }],
    ['SIZE', { width: 90 }],
    ['QTY', { width: 40 }, 'right'],
  ],
  expired: [
    ['', { width: 18 }],
    ['ITEM', { flex: 1 }],
    ['SIZE', { width: 90 }],
    ['QTY', { width: 40 }, 'right'],
    ['EXPIRED', { width: 70 }, 'right'],
  ],
  soon: [
    ['', { width: 18 }],
    ['ITEM', { flex: 1 }],
    ['SIZE', { width: 90 }],
    ['QTY', { width: 40 }, 'right'],
    ['EXPIRES', { width: 70 }, 'right'],
  ],
  equipment: [
    ['', { width: 18 }],
    ['EQUIPMENT', { flex: 1 }],
    ['STATUS', { width: 90 }],
    ['QTY', { width: 40 }, 'right'],
  ],
}

const ASSET_STATUS = {
  missing: 'Missing',
  removed: 'Removed',
  replaced: 'Replaced',
}

const fmtDate = (d) => (d ? moment(d).format('DD/MM/YYYY') : '')

// Only PNG/JPEG data or URLs render in react-pdf - any other uploaded logo
// format (webp, svg) would fail the whole download, so fall back to the name.
const usableLogo = (logo) => {
  if (!logo || typeof logo !== 'string') return null
  if (logo.startsWith('data:')) {
    return /^data:image\/(png|jpe?g);/i.test(logo) ? logo : null
  }
  return /\.(png|jpe?g)(\?|$)/i.test(logo) ? logo : null
}

// Same section + item + size (+ reason) collapse into one row with a count;
// keeps the order sections first appear in (the load list order).
const groupRows = (items, keyOf, build) => {
  const map = new Map()
  for (const item of items) {
    const key = keyOf(item)
    const existing = map.get(key)
    if (existing) build(existing, item)
    else map.set(key, build(null, item))
  }
  return [...map.values()]
}

const earliest = (a, b) => {
  if (!a) return b
  if (!b) return a
  return new Date(a) <= new Date(b) ? a : b
}

const stockRows = (items) =>
  groupRows(
    items,
    (i) => `${i.section}|${i.name}|${i.size || ''}`,
    (row, i) =>
      row
        ? Object.assign(row, {
            qty: row.qty + 1,
            date: earliest(row.date, i.expiryDate),
          })
        : {
            section: i.section,
            name: i.name,
            size: i.size || '',
            qty: 1,
            date: i.expiryDate,
          },
  )

const Cell = ({ col, children, bold }) => {
  const [, style, align] = col
  return (
    <View style={style}>
      <Text
        style={{
          textAlign: align || 'left',
          fontFamily: bold ? 'Helvetica-Bold' : 'Helvetica',
        }}
      >
        {children}
      </Text>
    </View>
  )
}

// A list rendered as a flat run of Page children (see header comment).
const listBlock = (kind, rows, hint, cellsFor) => {
  if (!rows.length) return []
  const tone = TONES[kind]
  const cols = COLUMNS[kind]
  const out = [
    <View key={`${kind}-head`} style={styles.blockHead} minPresenceAhead={70}>
      <View style={[styles.blockDot, { backgroundColor: tone.hex }]} />
      <Text style={styles.blockTitle}>{tone.label}</Text>
      <Text style={styles.blockHint}>{hint}</Text>
    </View>,
    <View
      key={`${kind}-cols`}
      style={[styles.colHead, { backgroundColor: tone.tint }]}
      minPresenceAhead={40}
      wrap={false}
    >
      {cols.map((col, idx) => (
        <View key={idx} style={col[1]}>
          <Text style={[styles.colHeadText, { textAlign: col[2] || 'left' }]}>
            {col[0]}
          </Text>
        </View>
      ))}
    </View>,
  ]
  let lastSection = null
  rows.forEach((row, idx) => {
    if (row.section !== lastSection) {
      lastSection = row.section
      out.push(
        <View
          key={`${kind}-sec-${idx}`}
          style={styles.sectionRow}
          minPresenceAhead={22}
          wrap={false}
        >
          <Text style={styles.sectionText}>
            {(row.section || 'General').toUpperCase()}
          </Text>
        </View>,
      )
    }
    out.push(
      <View
        key={`${kind}-row-${idx}`}
        style={[styles.row, idx % 2 ? { backgroundColor: SOFT } : null]}
        wrap={false}
      >
        <View style={cols[0][1]}>
          <View style={styles.box} />
        </View>
        {cellsFor(row, cols)}
      </View>,
    )
  })
  return out
}

const PickList = ({
  expiredItems = [],
  expiringSoonItems = [],
  missingItems = [],
  assetIssues = [],
  assetVehicle,
  loadListTitle,
  orgSettings,
  generatedBy,
}) => {
  const generatedAt = new Date()
  const subject = assetVehicle?.callSign ?? assetVehicle?.assetID ?? 'Make Ready'
  const logo = usableLogo(orgSettings?.logo)

  const pickRows = missingItems.map((i) => ({
    section: i.section,
    name: i.name,
    size: i.size || '',
    qty: i.missing || 1,
  }))
  const expiredRows = stockRows(expiredItems)
  const soonRows = stockRows(expiringSoonItems)
  const equipmentRows = groupRows(
    assetIssues,
    (i) => `${i.section}|${i.name}|${i.size}`,
    (row, i) =>
      row
        ? Object.assign(row, { qty: row.qty + (i.missing || 1) })
        : {
            section: i.section,
            name: i.name,
            status: ASSET_STATUS[i.size] || i.size || '',
            qty: i.missing || 1,
          },
  )

  const sum = (rows) => rows.reduce((n, r) => n + r.qty, 0)
  const tiles = [
    ['pick', sum(pickRows), 'to pick'],
    ['expired', sum(expiredRows), 'to remove'],
    ['soon', sum(soonRows), 'expiring in 30 days'],
    ['equipment', sum(equipmentRows), 'equipment issues'],
  ]
  const nothingToDo = tiles.every(([, n]) => n === 0)

  const stockCells = (row, cols) => [
    <Cell key="n" col={cols[1]} bold>
      {row.name}
    </Cell>,
    <Cell key="s" col={cols[2]}>
      {row.size}
    </Cell>,
    <Cell key="q" col={cols[3]} bold>
      {row.qty}
    </Cell>,
    ...(cols[4]
      ? [
          <Cell key="d" col={cols[4]}>
            {fmtDate(row.date)}
          </Cell>,
        ]
      : []),
  ]

  return (
    <Document title={`Pick list - ${subject}`} author={orgSettings?.orgName}>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View>
            {logo ? (
              <Image src={logo} style={styles.orgLogo} />
            ) : (
              <Text style={styles.orgName}>{orgSettings?.orgName || ''}</Text>
            )}
          </View>
          <View>
            <Text style={styles.kicker}>MAKE READY</Text>
            <Text style={styles.title}>Pick List</Text>
          </View>
        </View>
        <View style={styles.rule} />

        <View style={styles.subject} wrap={false}>
          <View>
            <Text style={styles.subjectLabel}>{subject}</Text>
            {!!loadListTitle && (
              <Text style={styles.subjectSub}>Load list: {loadListTitle}</Text>
            )}
          </View>
          <View>
            <Text style={styles.metaLabel}>GENERATED</Text>
            <Text style={styles.metaValue}>
              {moment(generatedAt).format('DD/MM/YYYY HH:mm')}
            </Text>
            {!!generatedBy && (
              <Text style={[styles.metaValue, { color: MUTED }]}>
                by {generatedBy}
              </Text>
            )}
          </View>
        </View>

        <View style={styles.tiles} wrap={false}>
          {tiles.map(([kind, value, label], idx) => (
            <View
              key={kind}
              style={[
                styles.tile,
                {
                  borderTopColor: TONES[kind].hex,
                  backgroundColor: value ? TONES[kind].tint : SOFT,
                  marginLeft: idx ? 8 : 0,
                },
              ]}
            >
              <Text
                style={[
                  styles.tileValue,
                  { color: value ? TONES[kind].hex : MUTED },
                ]}
              >
                {value}
              </Text>
              <Text style={styles.tileLabel}>{label}</Text>
            </View>
          ))}
        </View>

        {nothingToDo && (
          <View style={styles.empty} wrap={false}>
            <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 12 }}>
              Nothing to pick
            </Text>
            <Text style={{ marginTop: 3, color: SLATE }}>
              Everything on the load list is present and in date.
            </Text>
          </View>
        )}

        {listBlock(
          'pick',
          pickRows,
          'Short against the load list - collect from stores',
          stockCells,
        )}
        {listBlock(
          'expired',
          expiredRows,
          'Take out of service and dispose of per policy',
          stockCells,
        )}
        {listBlock(
          'soon',
          soonRows,
          'Replace now or plan the swap',
          stockCells,
        )}
        {listBlock(
          'equipment',
          equipmentRows,
          'Find, replace or report',
          (row, cols) => [
            <Cell key="n" col={cols[1]} bold>
              {row.name}
            </Cell>,
            <Cell key="s" col={cols[2]}>
              {row.status}
            </Cell>,
            <Cell key="q" col={cols[3]} bold>
              {row.qty}
            </Cell>,
          ],
        )}

        {!nothingToDo && (
          <View style={styles.signOff} wrap={false}>
            {['Picked by', 'Restocked by', 'Date'].map((label, idx) => (
              <View
                key={label}
                style={[styles.signField, idx === 2 ? { marginRight: 0 } : null]}
              >
                <View style={styles.signLine} />
                <Text style={styles.signLabel}>{label}</Text>
              </View>
            ))}
          </View>
        )}

        <View style={styles.footer} fixed>
          <View style={styles.poweredBy}>
            <Text style={styles.poweredText}>Powered by</Text>
            <Image src={ambunetWordmark} style={styles.wordmark} />
          </View>
          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) =>
              `${subject} - Pick list - Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  )
}

export default PickList
